/**
 * Timeline rows in words, for /timeline and a project's "Recently". The
 * server words its rows ('Marcus Reyes · SMS', 'Copy edit · Done') and masks
 * one-time codes; this keeps a row the server still sends in its raw joined
 * form ('in · sms · Marcus Reyes', 'copy_edit · done') readable too, and
 * capitalises bare workout types. Message titles are third-party text and pass
 * through as plain text.
 */
import { taskTypeLabel } from './agents.js';
import { humanize } from './format.js';
import type { TimelineItem } from './types.js';

const CHANNEL_LABEL: Readonly<Record<string, string>> = {
  sms: 'SMS',
  imessage: 'iMessage',
  email: 'Email',
  signal: 'Signal',
  whatsapp: 'WhatsApp',
  gmail: 'Email',
  phone: 'Phone',
};

export function channelLabel(channel: string): string {
  return CHANNEL_LABEL[channel.toLowerCase()] ?? humanize(channel);
}

/** 'Marcus Reyes · SMS' for an inbound message, 'You → Marcus Reyes · SMS' for an outbound one. */
export function messageDetail(direction: string, channel: string | null, name: string | null): string {
  const ch = channel ? channelLabel(channel) : null;
  const who = direction === 'out' ? (name ? `You → ${name}` : 'You') : (name ?? 'Unknown sender');
  return ch ? `${who} · ${ch}` : who;
}

const SEP = ' · ';

/** The title and detail line one timeline row shows. */
export function timelineText(it: TimelineItem): { title: string; detail: string | null } {
  const title = it.title ?? humanize(it.kind);
  switch (it.kind) {
    case 'message': {
      // Raw: 'direction · channel[ · contact name]'. Only the first two parts are
      // machine values (a raw channel is a lowercase key); the rest, rejoined, is
      // the name, which may itself contain ' · '.
      const parts = it.detail?.split(SEP) ?? [];
      if ((parts[0] === 'in' || parts[0] === 'out') && /^[a-z_]+$/.test(parts[1] ?? '')) {
        const name = parts.slice(2).join(SEP).trim();
        return { title, detail: messageDetail(parts[0], parts[1] ?? null, name || null) };
      }
      return { title, detail: it.detail };
    }
    case 'agent_run': {
      const m = /^([a-z0-9_]+) · ([a-z_]+)$/.exec(it.title ?? '');
      return m ? { title: `${taskTypeLabel(m[1]!)} · ${humanize(m[2]!)}`, detail: it.detail } : { title, detail: it.detail };
    }
    case 'workout':
      return { title: humanize(title), detail: it.detail };
    default:
      return { title, detail: it.detail };
  }
}
